import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Divider, IconButton, ListSubheader } from '@mui/material';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import Toolbar from '@mui/material/Toolbar';
import { useAuth } from 'providers/AuthProvider';
import { useSettingsContext } from 'providers/SettingsProvider';
import sitemap from 'routes/sitemap';
import { getCoupleSitemap, getGuestSitemap } from 'routes/wedding-sitemap';
import IconifyIcon from 'components/base/IconifyIcon';
import Logo from 'components/common/Logo';
import { useNavContext } from '../NavProvider';
import NavItem from './NavItem';
import SidenavSimpleBar from './SidenavSimpleBar';
import SidenavUpgradeCard from './SidenavUpgradeCard';
import SidenavPremiumBadge from './SidenavPremiumBadge';

interface SidenavDrawerContentProps {
  variant?: 'permanent' | 'temporary';
}

const SidenavDrawerContent = ({ variant = 'permanent' }: SidenavDrawerContentProps) => {
  const { t } = useTranslation();
  const { sessionUser } = useAuth();
  const {
    config: { sidenavCollapsed, openNavbarDrawer, navigationMenuType },
    setConfig,
  } = useSettingsContext();

  const { sidenavAppbarVariant } = useNavContext();

  const expanded = useMemo(
    () => variant === 'temporary' || (variant === 'permanent' && !sidenavCollapsed),
    [sidenavCollapsed],
  );

  // Select sitemap based on user role + plan tier (Heirloom unlocks the
  // invitation-product sidebar entries: Guests, Wishes, Quiz).
  const activeSitemap = useMemo(() => {
    if (!sessionUser) return sitemap; // Default Aurora sitemap for unauthenticated users

    const eventId = sessionUser.eventId;

    if (sessionUser.role === 'couple') {
      return getCoupleSitemap(eventId, sessionUser.plan ?? 'free', sessionUser.eventType);
    }
    if (sessionUser.role === 'uploader') {
      return getGuestSitemap(eventId, sessionUser.plan ?? 'free', sessionUser.eventType);
    }

    return sitemap; // Fallback to default
  }, [sessionUser]);

  const toggleNavbarDrawer = () => {
    setConfig({
      openNavbarDrawer: !openNavbarDrawer,
    });
  };

  return (
    <>
      <Toolbar variant={sidenavAppbarVariant} sx={{ display: 'block', px: { xs: 0 } }}>
        <Box
          sx={[
            {
              height: 1,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            },
            !expanded && {
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            },
            expanded && {
              pl: { xs: 4, md: 6 },
              pr: { xs: 2, md: 3 },
            },
          ]}
        >
          {(navigationMenuType === 'sidenav' || variant === 'temporary') && (
            <>
              <Logo showName={expanded} />
              <IconButton sx={{ mt: 1, display: { md: 'none' } }} onClick={toggleNavbarDrawer}>
                <IconifyIcon icon="material-symbols:left-panel-close-outline" fontSize={20} />
              </IconButton>
            </>
          )}
        </Box>
      </Toolbar>
      <Divider />
      <Box sx={{ flex: 1, overflow: 'hidden' }}>
        <SidenavSimpleBar>
          <Box
            sx={[
              {
                py: 2,
              },
              !expanded && {
                px: 2,
              },
              expanded && {
                px: { xs: 2, md: 4 },
              },
            ]}
          >
            {activeSitemap.map((menu, index) => (
              <Box key={menu.id}>
                {menu.subheader === 'Docs' && !sidenavCollapsed && (
                  <>
                    <Divider sx={{ mb: 4 }} />
                  </>
                )}
                <List
                  dense
                  key={menu.id}
                  sx={{
                    mb: index !== sitemap.length - 1 ? 3 : 0,
                    pb: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: (theme) => theme.spacing(1),
                  }}
                  subheader={
                    menu.subheader && (
                      <ListSubheader
                        component="div"
                        disableGutters
                        sx={{
                          textAlign: expanded ? 'left' : 'center',
                          color: 'text.disabled',
                          typography: 'overline',
                          fontWeight: 700,
                          textTransform: 'none',
                          py: 1,
                          paddingLeft: expanded ? 2 : 0,
                          mb: 0.25,
                          position: 'static',
                          background: 'transparent',
                        }}
                      >
                        {t(menu.key || menu.subheader)}
                      </ListSubheader>
                    )
                  }
                >
                  {menu.items.map((item) => (
                    <NavItem key={item.pathName} item={item} level={0} />
                  ))}
                </List>
              </Box>
            ))}
          </Box>{' '}
        </SidenavSimpleBar>
      </Box>

      {/* Upgrade card for free-plan couples */}
      {sessionUser?.role === 'couple' && sessionUser.plan && sessionUser.plan === 'free' && (
        <Box sx={[{ pt: 2 }, !expanded && { display: 'flex', justifyContent: 'center', pb: 2 }]}>
          <SidenavUpgradeCard
            storageUsed={sessionUser.storageUsed || 0}
            storageLimit={sessionUser.storageLimit || 1073741824}
            photoCap={sessionUser.photoCap}
            plan={sessionUser.plan || 'free'}
            collapsed={!expanded}
          />
        </Box>
      )}

      {/* Premium status badge for upgraded couples (with expiration awareness) */}
      {sessionUser?.role === 'couple' && sessionUser.plan && sessionUser.plan !== 'free' && (
        <Box sx={[{ pt: 2 }, !expanded && { display: 'flex', justifyContent: 'center', pb: 2 }]}>
          {sessionUser.subscriptionStatus === 'expired' ? (
            <SidenavUpgradeCard
              storageUsed={sessionUser.storageUsed || 0}
              storageLimit={sessionUser.storageLimit || 1073741824}
              plan={sessionUser.plan || 'free'}
              collapsed={!expanded}
              expired
            />
          ) : (
            <SidenavPremiumBadge plan={sessionUser.plan as 'basic' | 'pro'} collapsed={!expanded} />
          )}
        </Box>
      )}
    </>
  );
};

export default SidenavDrawerContent;
